import { useState, useEffect, useCallback } from 'react';
import { useAutoRefresh } from '../hooks/useAutoRefresh';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import {
    Plane, Search, Clock, MapPin, Users, Loader2,
    ArrowRight, Shield, ShieldCheck, ShieldX, FileText,
    AlertTriangle, CheckCircle, Package, Fuel, CloudSun, Eye, FileWarning
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { getLocalToday } from '../utils/dateUtils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusLabels = {
    scheduled: 'Programado', confirmed: 'Confirmado', preparing: 'Preparando',
    boarding: 'Abordando', closed: 'Cerrado', departed: 'Despegado',
    en_route: 'En Vuelo', arrived: 'Llegado', delayed: 'Demorado', cancelled: 'Cancelado',
};
const statusColors = {
    scheduled: 'bg-slate-500/10 text-slate-400',
    confirmed: 'bg-blue-500/10 text-blue-400',
    preparing: 'bg-cyan-500/10 text-cyan-400',
    boarding: 'bg-amber-500/10 text-amber-400',
    closed: 'bg-orange-500/10 text-orange-400',
    departed: 'bg-emerald-500/10 text-emerald-400',
};

export default function DispatchPage() {
    const { token, user, airline, hasRole } = useAuth();
    const fuelUnit = airline?.fuel_unit === 'kg' ? 'kg' : 'L';
    const headers = { Authorization: `Bearer ${token}` };

    const [flights, setFlights] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dateFilter, setDateFilter] = useState(getLocalToday());
    const [searchTerm, setSearchTerm] = useState('');

    // Dispatch release
    const [showRelease, setShowRelease] = useState(null);
    const [releaseData, setReleaseData] = useState({
        weather_brief: '', fuel_plan: '', fuel_kg: 0,
        notams: '', release_notes: '', alternate_airport: ''
    });
    const [submitting, setSubmitting] = useState(false);

    // Folder view
    const [showFolder, setShowFolder] = useState(null);
    const [folder, setFolder] = useState(null);
    const [folderLoading, setFolderLoading] = useState(false);

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const fRes = await axios.get(`${API}/dispatch/flights?date=${dateFilter}`, { headers });
            setFlights(fRes.data);
        } catch { toast.error('Error al cargar datos'); }
        finally { setLoading(false); }
    }, [dateFilter]);

    useEffect(() => { fetchData(); }, [fetchData]);
    // Auditoria 4 - Item 19: auto-refresh 30s
    useAutoRefresh(fetchData, 30000);

    const openFolder = async (flight) => {
        setShowFolder(flight);
        setFolderLoading(true);
        try {
            const res = await axios.get(`${API}/dispatch/flights/${flight.id}/folder`, { headers });
            setFolder(res.data);
        } catch { toast.error('Error al cargar carpeta operacional'); }
        finally { setFolderLoading(false); }
    };

    const releaseFlight = async () => {
        if (!showRelease) return;
        setSubmitting(true);
        const send = (extra = {}) => axios.post(`${API}/dispatch/flights/${showRelease.id}/release`, { ...releaseData, ...extra }, { headers });
        const done = () => {
            toast.success('Vuelo autorizado para despacho');
            setShowRelease(null);
            setReleaseData({ weather_brief: '', fuel_plan: '', fuel_kg: 0, notams: '', release_notes: '', alternate_airport: '' });
            fetchData();
        };
        const errMsg = (e) => { const d = e.response?.data?.detail; return typeof d === 'string' ? d : d?.message || 'Error al autorizar vuelo'; };
        try {
            await send();
            done();
        } catch (e) {
            const d = e.response?.data?.detail;
            if (e.response?.status === 409 && d?.type === 'open_deferrals') {
                if (window.confirm(d.message)) {
                    try { await send({ acknowledge_open_deferrals: true }); done(); } catch (e2) { toast.error(errMsg(e2)); }
                } else {
                    toast.info('Autorización cancelada');
                }
            } else {
                toast.error(errMsg(e), { duration: 10000 });
            }
        } finally { setSubmitting(false); }
    };

    const revokeRelease = async (flightId) => {
        try {
            await axios.delete(`${API}/dispatch/flights/${flightId}/release`, { headers });
            toast.success('Autorización revocada');
            fetchData();
        } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    };

    const filteredFlights = flights.filter(f => {
        if (!searchTerm) return true;
        const s = searchTerm.toLowerCase();
        return f.flight_number?.toLowerCase().includes(s) || f.origin?.toLowerCase().includes(s) || f.destination?.toLowerCase().includes(s);
    });

    const released = flights.filter(f => f.dispatch_released).length;
    const pending = flights.filter(f => !f.dispatch_released && f.aircraft_id && f.assigned_crew?.length > 0).length;
    const canDispatch = hasRole('admin') || hasRole('dispatcher');

    return (
        <div className="space-y-6" data-testid="dispatch-page">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="text-4xl font-bold tracking-tight uppercase font-['Barlow_Condensed']" data-testid="dispatch-title">
                        Despacho de Vuelo
                    </h1>
                    <p className="text-muted-foreground mt-1">Autorizacion de vuelos y carpeta operacional</p>
                </div>
                <div className="flex items-center gap-2">
                    <Input type="date" value={dateFilter} onChange={e => setDateFilter(e.target.value)} className="w-[160px] input-aviation font-mono" data-testid="dispatch-date-filter" />
                </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-3">
                <Card className="card-aviation">
                    <CardContent className="p-3 flex items-center gap-3">
                        <Plane className="w-5 h-5 text-primary" />
                        <div><p className="text-xl font-bold font-mono">{flights.length}</p><p className="text-[10px] text-muted-foreground uppercase">Vuelos Hoy</p></div>
                    </CardContent>
                </Card>
                <Card className="card-aviation">
                    <CardContent className="p-3 flex items-center gap-3">
                        <ShieldCheck className="w-5 h-5 text-emerald-500" />
                        <div><p className="text-xl font-bold font-mono">{released}</p><p className="text-[10px] text-muted-foreground uppercase">Autorizados</p></div>
                    </CardContent>
                </Card>
                <Card className="card-aviation">
                    <CardContent className="p-3 flex items-center gap-3">
                        <Shield className="w-5 h-5 text-amber-500" />
                        <div><p className="text-xl font-bold font-mono">{pending}</p><p className="text-[10px] text-muted-foreground uppercase">Listos p/Autorizar</p></div>
                    </CardContent>
                </Card>
            </div>

            {/* Flights List */}
            <div className="space-y-4">
                <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input placeholder="Buscar vuelo..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="pl-10 input-aviation" data-testid="dispatch-search" />
                </div>

                {loading ? (
                    <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>
                ) : (
                    <div className="space-y-3" data-testid="dispatch-flights-list">
                        {filteredFlights.length === 0 ? (
                            <Card className="card-aviation"><CardContent className="p-12 text-center text-muted-foreground"><Plane className="w-12 h-12 mx-auto mb-3 opacity-30" /><p>No hay vuelos</p></CardContent></Card>
                        ) : filteredFlights.map(f => {
                                const hasAC = !!f.aircraft_id;
                                const hasCrew = f.assigned_crew?.length > 0;
                                const isReady = hasAC && hasCrew;

                                return (
                                    <Card key={f.id} className="card-aviation" data-testid={`dispatch-flight-${f.id}`}>
                                        <CardContent className="p-4">
                                            <div className="flex items-center justify-between flex-wrap gap-3">
                                                <div className="flex items-center gap-4">
                                                    <div className="flex items-center gap-2">
                                                        {f.dispatch_released ? (
                                                            <ShieldCheck className="w-5 h-5 text-emerald-500" />
                                                        ) : isReady ? (
                                                            <Shield className="w-5 h-5 text-amber-500" />
                                                        ) : (
                                                            <ShieldX className="w-5 h-5 text-muted-foreground" />
                                                        )}
                                                        <div>
                                                            <p className="font-bold font-mono text-lg">{f.flight_number}</p>
                                                            <p className="text-xs text-muted-foreground">{f.scheduled_departure?.split('T')[1]?.slice(0,5) || '--:--'}</p>
                                                        </div>
                                                    </div>
                                                    <div className="flex items-center gap-1 text-sm">
                                                        <span className="font-mono font-bold">{f.origin}</span>
                                                        <ArrowRight className="w-3.5 h-3.5 text-muted-foreground" />
                                                        <span className="font-mono font-bold">{f.destination}</span>
                                                    </div>
                                                </div>

                                                <div className="flex items-center gap-2 flex-wrap">
                                                    {/* Readiness indicators */}
                                                    <span className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs ${hasAC ? 'bg-emerald-500/10 text-emerald-500' : 'bg-muted text-muted-foreground'}`}>
                                                        <Plane className="w-3 h-3" />{f.aircraft_registration || 'Sin A/C'}
                                                    </span>
                                                    <span className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs ${hasCrew ? 'bg-emerald-500/10 text-emerald-500' : 'bg-muted text-muted-foreground'}`}>
                                                        <Users className="w-3 h-3" />{hasCrew ? `${f.assigned_crew.length} crew` : 'Sin crew'}
                                                    </span>
                                                    <span className="flex items-center gap-1 px-2 py-1 text-xs text-muted-foreground">
                                                        <Users className="w-3 h-3" />{f.pax_checked_in || 0}/{f.pax_total || 0} PAX
                                                    </span>

                                                    <Badge className={statusColors[f.status] || 'bg-muted'}>{statusLabels[f.status] || f.status}</Badge>

                                                    {/* Actions */}
                                                    <Button variant="ghost" size="sm" className="h-7" onClick={() => openFolder(f)} data-testid={`folder-${f.id}`}>
                                                        <Eye className="w-3.5 h-3.5 mr-1" />Carpeta
                                                    </Button>

                                                    {canDispatch && !f.dispatch_released && isReady && (
                                                        <Button size="sm" className="h-7 btn-primary" onClick={() => setShowRelease(f)} data-testid={`release-btn-${f.id}`}>
                                                            <ShieldCheck className="w-3.5 h-3.5 mr-1" />Autorizar
                                                        </Button>
                                                    )}
                                                    {canDispatch && f.dispatch_released && (
                                                        <Button variant="outline" size="sm" className="h-7 text-destructive" onClick={() => revokeRelease(f.id)} data-testid={`revoke-${f.id}`}>
                                                            <ShieldX className="w-3.5 h-3.5 mr-1" />Revocar
                                                        </Button>
                                                    )}
                                                </div>
                                            </div>

                                            {f.dispatch_release && (
                                                <div className="mt-2 flex items-center gap-3 text-xs text-muted-foreground border-t border-border/30 pt-2">
                                                    <span className="text-emerald-500 font-medium">Autorizado por {f.dispatch_release.dispatcher_name}</span>
                                                    <span>{new Date(f.dispatch_release.released_at).toLocaleString('es-ES', { hour: '2-digit', minute: '2-digit' })}</span>
                                                    {f.dispatch_release.fuel_kg > 0 && <span><Fuel className="w-3 h-3 inline" /> {f.dispatch_release.fuel_kg}{fuelUnit}</span>}
                                                    {f.dispatch_release.alternate_airport && <span>Alt: {f.dispatch_release.alternate_airport}</span>}
                                                </div>
                                            )}
                                        </CardContent>
                                    </Card>
                                );
                            })}
                        </div>
                    )}
            </div>

            {/* Release Dialog */}
            <Dialog open={!!showRelease} onOpenChange={() => setShowRelease(null)}>
                <DialogContent className="sm:max-w-[550px] max-h-[85vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="font-['Barlow_Condensed'] text-xl uppercase tracking-tight flex items-center gap-2">
                            <ShieldCheck className="w-5 h-5 text-primary" />
                            Autorizar Vuelo — {showRelease?.flight_number}
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                        <p className="text-sm text-muted-foreground">
                            {showRelease?.origin} → {showRelease?.destination} | {showRelease?.scheduled_departure?.split('T')[0]} {showRelease?.scheduled_departure?.split('T')[1]?.slice(0,5)}
                        </p>
                        <div className="space-y-1.5">
                            <Label>Briefing Meteorologico</Label>
                            <Textarea value={releaseData.weather_brief} onChange={e => setReleaseData({...releaseData, weather_brief: e.target.value})} placeholder="Condiciones meteorologicas..." rows={3} data-testid="release-weather" />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-1.5">
                                <Label>Combustible ({fuelUnit})</Label>
                                <Input type="number" min="0" value={releaseData.fuel_kg} onChange={e => setReleaseData({...releaseData, fuel_kg: parseInt(e.target.value) || 0})} data-testid="release-fuel" />
                            </div>
                            <div className="space-y-1.5">
                                <Label>Alternativa</Label>
                                <Input value={releaseData.alternate_airport} onChange={e => setReleaseData({...releaseData, alternate_airport: e.target.value.toUpperCase()})} placeholder="ICAO" data-testid="release-alternate" />
                            </div>
                        </div>
                        <div className="space-y-1.5">
                            <Label>Plan de Combustible</Label>
                            <Textarea value={releaseData.fuel_plan} onChange={e => setReleaseData({...releaseData, fuel_plan: e.target.value})} placeholder="Trip fuel, contingency, reserves..." rows={2} data-testid="release-fuel-plan" />
                        </div>
                        <div className="space-y-1.5">
                            <Label>NOTAMs Relevantes</Label>
                            <Textarea value={releaseData.notams} onChange={e => setReleaseData({...releaseData, notams: e.target.value})} placeholder="NOTAMs aplicables..." rows={2} data-testid="release-notams" />
                        </div>
                        <div className="space-y-1.5">
                            <Label>Notas del Despacho</Label>
                            <Textarea value={releaseData.release_notes} onChange={e => setReleaseData({...releaseData, release_notes: e.target.value})} placeholder="Notas adicionales..." rows={2} data-testid="release-notes" />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowRelease(null)}>Cancelar</Button>
                        <Button onClick={releaseFlight} disabled={submitting} className="btn-primary" data-testid="submit-release">
                            {submitting && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                            <ShieldCheck className="w-4 h-4 mr-2" />Autorizar Vuelo
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Folder Dialog */}
            <Dialog open={!!showFolder} onOpenChange={() => setShowFolder(null)}>
                <DialogContent className="sm:max-w-[700px] max-h-[85vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="font-['Barlow_Condensed'] text-xl uppercase tracking-tight flex items-center gap-2">
                            <FileText className="w-5 h-5 text-primary" />
                            Carpeta Operacional — {showFolder?.flight_number}
                        </DialogTitle>
                    </DialogHeader>
                    {folderLoading ? (
                        <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
                    ) : folder ? (
                        <div className="space-y-4">
                            {/* Flight info */}
                            <div className="p-3 rounded-lg bg-muted/30 border border-border/30">
                                <h3 className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-2">Informacion del Vuelo</h3>
                                <div className="grid grid-cols-2 gap-2 text-sm">
                                    <div><span className="text-muted-foreground">Ruta:</span> <span className="font-mono font-bold">{folder.flight?.origin} → {folder.flight?.destination}</span></div>
                                    <div><span className="text-muted-foreground">Fecha:</span> <span className="font-mono">{folder.flight?.scheduled_departure?.split('T')[0]}</span></div>
                                    <div><span className="text-muted-foreground">Salida:</span> <span className="font-mono">{folder.flight?.scheduled_departure?.split('T')[1]?.slice(0,5)}</span></div>
                                    <div><span className="text-muted-foreground">Llegada:</span> <span className="font-mono">{folder.flight?.scheduled_arrival?.split('T')[1]?.slice(0,5)}</span></div>
                                    <div><span className="text-muted-foreground">Estado:</span> <Badge className={statusColors[folder.flight?.status]}>{statusLabels[folder.flight?.status]}</Badge></div>
                                </div>
                            </div>

                            {/* Aircraft */}
                            <div className="p-3 rounded-lg bg-muted/30 border border-border/30">
                                <h3 className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-2">Aeronave</h3>
                                {folder.aircraft ? (
                                    <div className="grid grid-cols-2 gap-2 text-sm">
                                        <div><span className="text-muted-foreground">Registro:</span> <span className="font-mono font-bold">{folder.aircraft.registration}</span></div>
                                        <div><span className="text-muted-foreground">Tipo:</span> <span>{folder.aircraft.aircraft_type}</span></div>
                                    </div>
                                ) : <p className="text-sm text-muted-foreground">Sin aeronave asignada</p>}
                            </div>

                            {/* Crew */}
                            <div className="p-3 rounded-lg bg-muted/30 border border-border/30">
                                <h3 className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-2">Tripulacion ({folder.crew?.length || 0})</h3>
                                {folder.crew?.length > 0 ? (
                                    <div className="space-y-1">
                                        {folder.crew.map(c => (
                                            <div key={c.id} className="flex items-center justify-between text-sm">
                                                <span>{c.name || `${c.first_name} ${c.last_name}`}</span>
                                                <span className="text-xs text-muted-foreground">{c.position} {c.license_number ? `— ${c.license_number}` : ''}</span>
                                            </div>
                                        ))}
                                    </div>
                                ) : <p className="text-sm text-muted-foreground">Sin tripulacion asignada</p>}
                            </div>

                            {/* PAX Summary */}
                            <div className="p-3 rounded-lg bg-muted/30 border border-border/30">
                                <h3 className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-2">Pasajeros</h3>
                                <div className="grid grid-cols-3 gap-2 text-sm">
                                    <div><span className="text-muted-foreground">Total:</span> <span className="font-bold">{folder.pax_summary?.total || 0}</span></div>
                                    <div><span className="text-muted-foreground">Check-in:</span> <span className="font-bold">{folder.pax_summary?.checked_in || 0}</span></div>
                                    <div><span className="text-muted-foreground">Abordados:</span> <span className="font-bold">{folder.pax_summary?.boarded || 0}</span></div>
                                    <div><span className="text-muted-foreground">ADL:</span> {folder.pax_summary?.adults || 0}</div>
                                    <div><span className="text-muted-foreground">CHD:</span> {folder.pax_summary?.children || 0}</div>
                                    <div><span className="text-muted-foreground">INF:</span> {folder.pax_summary?.infants || 0}</div>
                                    <div className="col-span-3"><span className="text-muted-foreground">Equipaje:</span> {folder.pax_summary?.total_bags || 0} pzas / {folder.pax_summary?.total_bag_weight_kg || 0}kg</div>
                                </div>
                            </div>

                            {/* Deferrals */}
                            {folder.deferrals?.length > 0 && (
                                <div className="p-3 rounded-lg bg-amber-500/5 border border-amber-500/20">
                                    <h3 className="text-xs font-bold uppercase tracking-widest text-amber-500 mb-2 flex items-center gap-1">
                                        <AlertTriangle className="w-3.5 h-3.5" />Diferidos Activos ({folder.deferrals.length})
                                    </h3>
                                    {folder.deferrals.map(d => (
                                        <div key={d.id} className="text-sm text-muted-foreground">{d.description || d.title || 'Diferido'}</div>
                                    ))}
                                </div>
                            )}

                            {/* Dispatch Release */}
                            {folder.dispatch_release ? (
                                <div className="p-3 rounded-lg bg-emerald-500/5 border border-emerald-500/20">
                                    <h3 className="text-xs font-bold uppercase tracking-widest text-emerald-500 mb-2 flex items-center gap-1">
                                        <ShieldCheck className="w-3.5 h-3.5" />Autorizacion de Despacho
                                    </h3>
                                    <div className="grid grid-cols-2 gap-2 text-sm">
                                        <div><span className="text-muted-foreground">Despachador:</span> {folder.dispatch_release.dispatcher_name}</div>
                                        <div><span className="text-muted-foreground">Hora:</span> {new Date(folder.dispatch_release.released_at).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}</div>
                                        {folder.dispatch_release.fuel_kg > 0 && <div><span className="text-muted-foreground">Combustible:</span> {folder.dispatch_release.fuel_kg} kg</div>}
                                        {folder.dispatch_release.alternate_airport && <div><span className="text-muted-foreground">Alternativa:</span> {folder.dispatch_release.alternate_airport}</div>}
                                    </div>
                                    {folder.dispatch_release.weather_brief && <p className="text-xs text-muted-foreground mt-2"><span className="font-bold">Meteo:</span> {folder.dispatch_release.weather_brief}</p>}
                                    {folder.dispatch_release.fuel_plan && <p className="text-xs text-muted-foreground mt-1"><span className="font-bold">Plan de combustible:</span> {folder.dispatch_release.fuel_plan}</p>}
                                    {folder.dispatch_release.notams && <p className="text-xs text-muted-foreground mt-1"><span className="font-bold">NOTAMs:</span> {folder.dispatch_release.notams}</p>}
                                    {folder.dispatch_release.release_notes && <p className="text-xs text-muted-foreground mt-1"><span className="font-bold">Notas:</span> {folder.dispatch_release.release_notes}</p>}
                                </div>
                            ) : (
                                <div className="p-3 rounded-lg bg-muted/30 border border-border/30 text-center text-muted-foreground text-sm">
                                    <ShieldX className="w-5 h-5 mx-auto mb-1 opacity-50" />
                                    Vuelo no autorizado aun
                                </div>
                            )}

                            {/* Auditoria 6 Fase C - Item 11: Diferidos MEL/CDL activos visibles en la Carpeta Operacional */}
                            {folder.deferrals && folder.deferrals.length > 0 && (
                                <div className="p-3 rounded-lg bg-amber-500/5 border border-amber-500/30">
                                    <h3 className="text-xs font-bold uppercase tracking-widest text-amber-500 mb-2 flex items-center gap-1">
                                        <FileWarning className="w-3.5 h-3.5" />Diferidos MEL/CDL activos ({folder.deferrals.length})
                                    </h3>
                                    <div className="space-y-1.5">
                                        {folder.deferrals.map(d => (
                                            <div key={d.id} className="flex items-start justify-between gap-2 text-xs bg-background/50 border border-amber-500/20 rounded px-2 py-1.5" data-testid={`folder-deferral-${d.id}`}>
                                                <div className="min-w-0 flex-1">
                                                    <div className="flex items-center gap-1.5">
                                                        <Badge className="bg-amber-500/15 text-amber-500 text-[10px] uppercase border-amber-500/30" variant="outline">Cat {d.category || 'B'}</Badge>
                                                        {d.mel_reference && <span className="font-mono text-amber-600">{d.mel_reference}</span>}
                                                        {d.ata_chapter && <span className="text-muted-foreground">ATA {d.ata_chapter}</span>}
                                                    </div>
                                                    <p className="text-muted-foreground truncate">{d.description || '—'}</p>
                                                    {d.operational_limitations && <p className="text-amber-500 text-[10px]">Limitación: {d.operational_limitations}</p>}
                                                </div>
                                                {d.due_date && (
                                                    <span className="text-[10px] text-muted-foreground shrink-0">
                                                        Vence: {String(d.due_date).slice(0, 10)}
                                                    </span>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Ramp Services Status */}
                            {folder.ramp_services ? (
                                <div className={`p-3 rounded-lg border ${folder.ramp_services.completed ? 'bg-emerald-500/5 border-emerald-500/20' : 'bg-amber-500/5 border-amber-500/20'}`}>
                                    <h3 className={`text-xs font-bold uppercase tracking-widest mb-2 flex items-center gap-1 ${folder.ramp_services.completed ? 'text-emerald-500' : 'text-amber-500'}`}>
                                        <Package className="w-3.5 h-3.5" />Servicios de Rampa {folder.ramp_services.completed ? '(Completo)' : '(En Progreso)'}
                                    </h3>
                                    <div className="grid grid-cols-2 gap-2 text-sm">
                                        {folder.ramp_services.fuel_liters > 0 && <div><span className="text-muted-foreground">Combustible:</span> <span className="font-mono">{folder.ramp_services.fuel_liters} {fuelUnit}</span></div>}
                                        {folder.ramp_services.cargo_kg > 0 && <div><span className="text-muted-foreground">Carga:</span> <span className="font-mono">{folder.ramp_services.cargo_kg} kg</span></div>}
                                        <div><span className="text-muted-foreground">Agente:</span> {folder.ramp_services.ramp_agent_name}</div>
                                    </div>
                                </div>
                            ) : (
                                <div className="p-3 rounded-lg bg-muted/30 border border-border/30 text-center text-muted-foreground text-sm">
                                    <Package className="w-5 h-5 mx-auto mb-1 opacity-50" />
                                    Sin servicios de rampa registrados
                                </div>
                            )}

                            {/* Auditoria 7 - Item 31: comparativa combustible autorizado vs cargado */}
                            {folder.fuel_comparison && folder.fuel_comparison.authorized_kg > 0 && (
                                <div className={`p-3 rounded-lg border ${folder.fuel_comparison.consistent ? 'bg-emerald-500/5 border-emerald-500/20' : 'bg-red-500/10 border-red-500/40'}`}
                                     data-testid="fuel-comparison-panel">
                                    <h3 className={`text-xs font-bold uppercase tracking-widest mb-2 flex items-center gap-1 ${folder.fuel_comparison.consistent ? 'text-emerald-500' : 'text-red-500'}`}>
                                        <Fuel className="w-3.5 h-3.5" />Consistencia de Combustible {folder.fuel_comparison.consistent ? '✓' : '⚠'}
                                    </h3>
                                    <div className="grid grid-cols-3 gap-2 text-sm">
                                        <div>
                                            <p className="text-[10px] uppercase text-muted-foreground">Autorizado</p>
                                            <p className="font-mono font-bold">{folder.fuel_comparison.authorized_kg} kg</p>
                                        </div>
                                        <div>
                                            <p className="text-[10px] uppercase text-muted-foreground">Cargado (Rampa)</p>
                                            <p className="font-mono font-bold">{folder.fuel_comparison.loaded_qty} {folder.fuel_comparison.loaded_unit}</p>
                                            {folder.fuel_comparison.loaded_unit !== 'kg' && (
                                                <p className="text-[10px] text-muted-foreground">≈ {folder.fuel_comparison.estimated_loaded_kg} kg</p>
                                            )}
                                        </div>
                                        <div>
                                            <p className="text-[10px] uppercase text-muted-foreground">Δ</p>
                                            <p className={`font-mono font-bold ${folder.fuel_comparison.consistent ? 'text-emerald-500' : 'text-red-500'}`}>
                                                {folder.fuel_comparison.diff_kg > 0 ? '+' : ''}{folder.fuel_comparison.diff_kg} kg
                                            </p>
                                        </div>
                                    </div>
                                    {folder.fuel_comparison.warning && (
                                        <p className="text-xs text-red-400 mt-2">{folder.fuel_comparison.warning}</p>
                                    )}
                                </div>
                            )}
                        </div>
                    ) : null}
                </DialogContent>
            </Dialog>
        </div>
    );
}
